import { useState } from 'react';
import { T, useGT } from 'gt-react';
import type { WorkbookTable } from '@erp/shared-types';
import type { WorkbookEditor } from './useWorkbookEditor';
import { SourcePanel } from './WorkbookInputs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export function AddWorksheetPanel({
  editor,
  addSheet,
  openItem,
}: {
  editor: WorkbookEditor;
  openItem: (id: string) => void;
  addSheet: (input: { id: string; name: string }, table: WorkbookTable) => void;
}) {
  const gt = useGT();
  const [sourceId, setSourceId] = useState('');
  const source = editor.definition.sources.find((item) => item.id === sourceId);
  return (
    <div className="space-y-3">
      <h2 className="font-medium">
        <T context="workbook add worksheet heading">Add a worksheet</T>
      </h2>
      <p className="text-sm text-muted-foreground">
        <T context="workbook new worksheet guidance">
          Choose a source to create a worksheet from. To build on an existing
          worksheet, open it and choose Next worksheet.
        </T>
      </p>
      {editor.definition.sources.length ? (
        <Select value={sourceId || null} onValueChange={setSourceId}>
          <SelectTrigger
            className="min-w-56"
            aria-label={gt('Worksheet data', {
              context: 'workbook new worksheet input selector',
            })}
          >
            <SelectValue
              placeholder={gt('Select data', {
                context: 'workbook new worksheet input placeholder',
              })}
            />
          </SelectTrigger>
          <SelectContent>
            {editor.definition.sources.map((input) => (
              <SelectItem key={input.id} value={input.id}>
                {input.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        <p className="text-sm text-muted-foreground">
          <T context="workbook no inputs for worksheet">
            Add a source first using the plus button beside Data.
          </T>
        </p>
      )}
      {source && (
        <SourcePanel
          key={source.id}
          editor={editor}
          source={source}
          addSheet={addSheet}
          openItem={openItem}
        />
      )}
    </div>
  );
}
